import { create } from 'zustand';
import type { DriverSettlementPendingSummary } from '../../types/settlement';

export type ActiveView = 'pending' | 'draft' | 'statements';

interface SettlementsState {
  // Active tab/view — persists across navigation
  activeView: ActiveView;

  // Selected driver for detail view
  selectedDriver: DriverSettlementPendingSummary | null;

  // Selected paystub for detail view (persists across tab switches)
  selectedPaystubId: string | null;

  // Expanded drivers
  expandedDrivers: Set<string>;

  // Selected settlements (for bulk actions)
  selectedSettlements: Set<string>;

  // Filters
  driverTypeFilter: string;
  searchQuery: string;
}

interface SettlementsActions {
  toggleDriverExpanded: (driverId: string) => void;
  setExpandedDrivers: (ids: Set<string>) => void;
  expandAll: (driverIds: string[]) => void;
  collapseAll: () => void;

  toggleSettlementSelected: (settlementId: string) => void;
  toggleAllForDriver: (settlementIds: string[]) => void;
  clearSelectedSettlements: () => void;

  setActiveView: (view: ActiveView) => void;
  setSelectedDriver: (driver: DriverSettlementPendingSummary | null) => void;
  setSelectedPaystubId: (id: string | null) => void;
  setDriverTypeFilter: (filter: string) => void;
  setSearchQuery: (query: string) => void;

  resetAll: () => void;
}

type SettlementsStore = SettlementsState & SettlementsActions;

const initialState: SettlementsState = {
  activeView: 'pending' as ActiveView,
  selectedDriver: null,
  selectedPaystubId: null,
  expandedDrivers: new Set<string>(),
  selectedSettlements: new Set<string>(),
  driverTypeFilter: 'all',
  searchQuery: '',
};

export const useSettlementsStore = create<SettlementsStore>()((set) => ({
  ...initialState,

  toggleDriverExpanded: (driverId) =>
    set((state) => {
      const isClosing = state.expandedDrivers.has(driverId);
      const next = new Set<string>();
      if (!isClosing) {
        next.add(driverId);
      }
      return { expandedDrivers: next, selectedSettlements: new Set() };
    }),

  setExpandedDrivers: (expandedDrivers) => set({ expandedDrivers }),

  expandAll: (driverIds) => set({ expandedDrivers: new Set(driverIds), selectedSettlements: new Set() }),

  collapseAll: () => set({ expandedDrivers: new Set(), selectedSettlements: new Set() }),

  toggleSettlementSelected: (settlementId) =>
    set((state) => {
      const next = new Set(state.selectedSettlements);
      if (next.has(settlementId)) {
        next.delete(settlementId);
      } else {
        next.add(settlementId);
      }
      return { selectedSettlements: next };
    }),

  toggleAllForDriver: (settlementIds) =>
    set((state) => {
      const next = new Set(state.selectedSettlements);
      const allSelected = settlementIds.every((id) => next.has(id));
      if (allSelected) {
        for (const id of settlementIds) next.delete(id);
      } else {
        for (const id of settlementIds) next.add(id);
      }
      return { selectedSettlements: next };
    }),

  clearSelectedSettlements: () => set({ selectedSettlements: new Set() }),

  setActiveView: (activeView) => set({ activeView }),

  setSelectedDriver: (selectedDriver) => set({ selectedDriver }),

  setSelectedPaystubId: (selectedPaystubId) => set({ selectedPaystubId }),

  setDriverTypeFilter: (driverTypeFilter) => set({ driverTypeFilter }),

  setSearchQuery: (searchQuery) => set({ searchQuery }),

  resetAll: () => set(initialState),
}));
